import { OrgPicker } from '@/components/OrgPicker/OrgPicker'
import {
  type ClusterUpdateFormData,
  useClusterConfigRequest,
} from '@/contexts/useConfigCreateRequest'
import { useProductCatalog } from '@/contexts/useProductCatalog'
import { useTemplates } from '@/contexts/useTemplates'
import { DeploymentLabelCombobox } from '@/features/ClusterConfigs/components/DeploymentLabelCombobox'
import { ProductSelect } from '@/features/ClusterConfigs/components/ProductSelect/ProductSelect'
import { TemplateCombobox } from '@/features/ClusterConfigs/components/TemplateCombobox/TemplateCombobox'
import { Field, Input, Text, VStack } from '@chakra-ui/react'
import React, { useImperativeHandle, useState } from 'react'
import { clusterStep1Schema } from './clusterStep1Schema'

export const NewClusterStep1System = React.forwardRef<
  unknown,
  Record<string, unknown>
>((_, ref) => {
  const [clusterNameHasBeenFocused, setClusterNameHasBeenFocused] =
    useState(false)

  const { request, onChange, errors, setErrors } = useClusterConfigRequest()
  const { templatesState } = useTemplates()
  const productCatalog = useProductCatalog()

  const currentTemplateIdAndLabel = React.useMemo(() => {
    const foundTemplate = (templatesState.data || []).find(
      ({ template_id }) => template_id === request.template_id,
    )
    if (!foundTemplate) {
      return
    }

    return {
      value: foundTemplate.template_id,
      label: foundTemplate.name ?? foundTemplate.template_id,
    }
  }, [templatesState.data, request.template_id])

  const currentProductIdAndLabel = React.useMemo(() => {
    if (!request.product_id) {
      return undefined
    }
    const foundProduct = productCatalog.data?.find(
      (product) => product.id === request.product_id,
    )
    if (!foundProduct || foundProduct.id == null) {
      return undefined
    }

    return {
      value: foundProduct.id,
      label: String(foundProduct.name),
    }
  }, [productCatalog.data, request.product_id])

  function validateField<K extends keyof ClusterUpdateFormData>(fieldName: K) {
    const validationResult = clusterStep1Schema.shape[fieldName]?.safeParse(
      request[fieldName],
    )

    if (!validationResult) {
      return
    }

    if (validationResult.success) {
      setErrors({
        ...errors,
        [fieldName]: '',
      })
      return
    }

    setErrors({
      ...errors,
      [fieldName]: validationResult.error.errors[0].message,
    })
  }

  function validateForm() {
    const validationResult = clusterStep1Schema.safeParse(request)

    if (!validationResult.success) {
      const validationErrors = validationResult.error.errors.reduce<
        Record<string, string>
      >((acc, err) => {
        if (err.path.length) {
          const key = err.path.join('.')
          acc[key] = err.message
        }
        return acc
      }, {})
      setErrors(validationErrors)
      return false
    }

    setErrors({})
    return true
  }

  useImperativeHandle(ref, () => ({
    validateForm,
  }))

  const handleClusterNameChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { value } = event.target
    onChange('_clusterName', value.trim())
  }

  function handleOrganizationChange(orgId: string) {
    onChange('_organizationId', orgId)
    if (orgId !== '' && errors._organizationId) {
      setErrors((current) => ({ ...current, _organizationId: '' }))
    }
  }

  function handleClusterNameFocus() {
    setClusterNameHasBeenFocused(true)
  }

  function handleClusterNameBlur() {
    if (clusterNameHasBeenFocused) {
      validateField('_clusterName')
      setClusterNameHasBeenFocused(false)
    }
  }

  return (
    <VStack data-testid="step-system" gap={6}>
      <Field.Root required invalid={Boolean(errors._clusterName)}>
        <Field.Label w={36}>Name</Field.Label>
        <Input
          id="name"
          name="name"
          placeholder="Enter a name for your cluster"
          value={request._clusterName}
          onChange={handleClusterNameChange}
          onBlur={handleClusterNameBlur}
          onFocus={handleClusterNameFocus}
        />
        {errors._clusterName ? (
          <Field.ErrorText>
            <Text>{errors._clusterName}</Text>
          </Field.ErrorText>
        ) : (
          <Field.HelperText>
            Cluster name must be at least 5 characters long and use only
            lowercase letters.
          </Field.HelperText>
        )}
      </Field.Root>

      <OrgPicker
        id="organization-id"
        label="Organization ID"
        required
        value={request._organizationId}
        onChange={handleOrganizationChange}
        onBlur={() => validateField('_organizationId')}
        error={errors._organizationId}
      />

      <ProductSelect
        onChange={(value) => onChange('product_id', value ?? undefined)}
        value={currentProductIdAndLabel?.value}
        error={errors.product_id}
        required
      />
      <TemplateCombobox
        value={currentTemplateIdAndLabel?.value}
        onChange={(value) => onChange('template_id', value ?? '')}
        error={errors.template_id}
        required
      />
      <DeploymentLabelCombobox
        value={request._deploymentLabel}
        onChange={(value) => onChange('_deploymentLabel', value)}
        error={errors._deploymentLabel}
        required
      />
    </VStack>
  )
})
